import React, { useEffect, useState, useCallback } from 'react';
import { INK } from '../../Utils/styleTokens';
import { getClusterHealth, getErrorHistory } from '../api';

// Health & Errors screen: per-service health probes + install-wide failed-job history.
// Not live log streaming — it shows the persisted failure record (error code + upstream body).
const HealthScreen = () => {
  const [health, setHealth] = useState([]);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthErr, setHealthErr] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);

  const [errors, setErrors] = useState([]);
  const [errorsLoading, setErrorsLoading] = useState(true);
  const [errorsErr, setErrorsErr] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthErr(null);
    try {
      const rows = await getClusterHealth();
      setHealth(rows);
      setCheckedAt(new Date());
    } catch (e) {
      setHealthErr(e?.message || 'Failed to load health');
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadErrors = useCallback(async () => {
    setErrorsLoading(true);
    setErrorsErr(null);
    try {
      const page = await getErrorHistory({ page: 0, size: 50 });
      setErrors(page.content || []);
    } catch (e) {
      setErrorsErr(e?.message || 'Failed to load error history');
    } finally {
      setErrorsLoading(false);
    }
  }, []);

  useEffect(() => { loadHealth(); loadErrors(); }, [loadHealth, loadErrors]);

  return (
    <div>
      <h1 className='adv-config-screen-title'>Health &amp; Errors</h1>
      <p className='adv-config-screen-desc'>
        Live reachability of registered services and a history of failed jobs across the install.
      </p>

      <div className='adv-config-card'>
        <div className='adv-config-toolbar'>
          <h2 style={{ fontSize: 15, margin: 0 }}>Service health</h2>
          <span style={{ flex: 1 }} />
          {checkedAt && <span style={{ fontSize: 12, color: INK[500] }}>Checked {checkedAt.toLocaleTimeString()}</span>}
          <button type='button' className='adv-config-btn-secondary adv-config-btn'
            onClick={loadHealth} disabled={healthLoading}>
            {healthLoading ? 'Checking…' : 'Refresh'}
          </button>
        </div>
        {healthErr && <div className='adv-config-msg-err' style={{ marginBottom: 10 }}>{healthErr}</div>}
        <table className='adv-config-table'>
          <thead>
            <tr>
              <th>Service</th>
              <th style={{ width: 110 }}>Status</th>
              <th style={{ width: 90 }}>Enabled</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {health.map((s) => (
              <tr key={s.id}>
                <td>{s.name || s.id}<span style={{ color: INK[400], marginLeft: 8, fontSize: 11 }}>{s.id}</span></td>
                <td className={s.ok ? 'adv-config-status-ok' : 'adv-config-status-bad'}>
                  {s.ok ? '● Healthy' : '● Down'}
                </td>
                <td>{s.enabled ? 'Yes' : 'No'}</td>
                <td style={{ color: INK[500], fontSize: 12 }}>{s.message}</td>
              </tr>
            ))}
            {!healthLoading && health.length === 0 && (
              <tr><td colSpan={4} style={{ color: INK[500] }}>No registered services.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='adv-config-card'>
        <div className='adv-config-toolbar'>
          <h2 style={{ fontSize: 15, margin: 0 }}>Recent errors</h2>
          <span style={{ flex: 1 }} />
          <button type='button' className='adv-config-btn-secondary adv-config-btn'
            onClick={loadErrors} disabled={errorsLoading}>
            {errorsLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {errorsErr && <div className='adv-config-msg-err' style={{ marginBottom: 10 }}>{errorsErr}</div>}
        <table className='adv-config-table'>
          <thead>
            <tr>
              <th style={{ width: 160 }}>When</th>
              <th style={{ width: 100 }}>Type</th>
              <th>Job / Source</th>
              <th style={{ width: 150 }}>Error</th>
              <th style={{ width: 70 }}>HTTP</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {errors.map((e) => (
              <React.Fragment key={e.id}>
                <tr>
                  <td style={{ fontSize: 12 }}>{formatTime(e.finishedAt || e.startedAt)}</td>
                  <td>{e.jobType}</td>
                  <td style={{ fontSize: 12 }}>
                    {e.runLabel || `#${e.id}`}
                    {e.registeredServiceId && <span style={{ color: INK[400], marginLeft: 6 }}>{e.registeredServiceId}</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{e.errorCode || '—'}</td>
                  <td>{e.upstreamHttpStatus ?? '—'}</td>
                  <td>
                    {(e.upstreamErrorBody || e.errorCode) && (
                      <button type='button' className='adv-config-btn-secondary adv-config-btn'
                        onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                        {expanded === e.id ? 'Hide' : 'Detail'}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === e.id && (
                  <tr>
                    <td colSpan={6}>
                      <div style={{ background: INK[50], padding: 12, borderRadius: 6 }}>
                        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 8, fontSize: 12, color: INK[500] }}>
                          <span><strong>Error:</strong> {e.errorCode || '—'}</span>
                          <span><strong>HTTP:</strong> {e.upstreamHttpStatus ?? '—'}</span>
                          {e.registeredServiceId && <span><strong>Service:</strong> {e.registeredServiceId}</span>}
                          <span style={{ flex: 1 }} />
                          {e.upstreamErrorBody && (
                            <button type='button' className='adv-config-btn-secondary adv-config-btn'
                              onClick={() => copyToClipboard(e.upstreamErrorBody)}>
                              Copy
                            </button>
                          )}
                        </div>
                        {/* Full stack trace / error output as returned by the worker (up to 16k
                            chars, stored server-side). Scrolls; shown untruncated. */}
                        <div className='adv-config-mono' style={{ maxHeight: 460, overflow: 'auto' }}>
                          {e.upstreamErrorBody || 'No additional detail was captured for this failure.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!errorsLoading && errors.length === 0 && (
              <tr><td colSpan={6} style={{ color: INK[500] }}>No failed jobs recorded.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function copyToClipboard(text) {
  try {
    if (navigator && navigator.clipboard) navigator.clipboard.writeText(text || '');
  } catch (e) {
    // best-effort; clipboard may be unavailable over http or in some browsers
    console.log('copy failed', e);
  }
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

export default HealthScreen;
